<template>
  <div>
    <Modal modalId="manage-plan-modal" size="modal-md">
      <ModalHeader :title="$t('xManagePlan')" icon="far fa-circle-dollar colorOrange mr-1">
        <div class="cols-auto">
          <button
            type="button"
            class="btn btn-icon-button btn-icon-button--gray"
            @click="closeManagePlanModal()"
            data-test-hook="close-manage-plan-modal"
          >
            <i class="fas fa-times" />
          </button>
        </div>
      </ModalHeader>
      <ModalBody>
        <div class="custom-control custom-radio mt-2 mb-3">
          <input
            class="custom-control-input"
            type="radio"
            :disabled="shouldDisableCadenceChange"
            id="changePaymentCadence"
            v-model="managePlanType"
            value="changePaymentCadence"
            data-test-hook="radio-change-payment-cadence"
          />
          <label class="custom-control-label" for="changePaymentCadence">
            {{ $t('xChangePaymentCadence') }}
          </label>
          <p class="text-small radio-button-subtext" v-if="!shouldDisableCadenceChange">
            {{ $t('xChangePaymentCadenceSubtext') }}
          </p>
          <p v-else class="text-small radio-button-subtext">
            {{ $t('xCadenceChangeDisabled') }}
          </p>
        </div>
        <div v-if="shouldShowRemoveAndDowngradeOptions()" class="custom-control custom-radio mb-3">
          <input
            class="custom-control-input"
            type="radio"
            id="removeProducts"
            v-model="managePlanType"
            value="removeProducts"
            data-test-hook="radio-remove-products"
          />
          <label class="custom-control-label" for="removeProducts">
            {{ $t('xRemoveProducts') }}
          </label>
          <p class="text-small radio-button-subtext">
            {{ $t('xRemoveProductsSubtext') }}
          </p>
        </div>
        <div v-if="shouldShowRemoveAndDowngradeOptions()" class="custom-control custom-radio mb-3">
          <input
            class="custom-control-input"
            type="radio"
            id="downgradePlan"
            v-model="managePlanType"
            value="downgradePlan"
            data-test-hook="radio-downgrade-plan"
          />
          <label class="custom-control-label" for="downgradePlan">
            {{ $t('xDowngradePlan') }}
          </label>
          <p class="text-small radio-button-subtext">
            {{ $t('xDowngradePlanSubtext') }}
          </p>
        </div>
        <div class="custom-control custom-radio">
          <input
            class="custom-control-input"
            type="radio"
            id="cancelPlan"
            v-model="managePlanType"
            value="cancelPlan"
            data-test-hook="radio-cancel-plan"
          />
          <label class="custom-control-label" for="cancelPlan">
            {{ $t('xCancelPlanLowercase') }}
          </label>
          <p class="text-small radio-button-subtext">
            {{ $t('xCancelPlanSubtext') }}
          </p>
        </div>
      </ModalBody>
      <ModalFooter>
        <button
          class="btn btn-outline-warning flex flex-grow-1"
          @click="closeManagePlanModal()"
          data-test-hook="cancel-button-manage-plan-modal"
        >
          {{ $t('xCancel') }}
        </button>
        <button
          class="btn btn-warning flex flex-grow-1"
          :disabled="!managePlanType"
          @click="goToSelectedOption()"
          data-test-hook="next-button-manage-plan-modal"
        >
          {{ $t('xNext') }}
          <span class="pl-1">
            <i class="far fa-arrow-right"></i>
          </span>
        </button>
      </ModalFooter>
    </Modal>

    <PaymentCadenceModal
      :endDate="subscription.subscriptionRenewalDate"
      :subscriptionStore="subscription.subscriptionStore !== undefined ? subscription.subscriptionStore : null"
    />

    <RemoveProductsSelectionModal :subscription="subscription" :account-profile="accountProfile" />

    <CancelSubscriptionModal
      :isCancelLegacy="false"
      :endDate="subscription.subscriptionRenewalDate"
      :cancelLegacyApparatusId="null"
      :cancelLegacyApparatusType="null"
      :cancelSubscriptionApparatuses="subscription.apparatuses !== null ? subscription.apparatuses : []"
      :cancelSubscriptionProductId="
        subscription.subscriptionProductId !== undefined ? subscription.subscriptionProductId : null
      "
      :cancelSubscriptionStore="subscription.subscriptionStore !== undefined ? subscription.subscriptionStore : null"
    />

    <DowngradeSubscriptionModal
      :currentSubscription="subscription"
      :newSubscription="newSubscription"
      @openPayment="proceedToPaymentForDowngrade"
    />

    <DowngradeUnavailableModal
      :account-profile="accountProfile"
      :subscription-products="allSubscriptionProducts"
      @mustRemoveBeforeDowngrading="mustRemoveBeforeDowngrading"
    />

    <!-- Used for downgrade only on this page. Cadence change and cancel are handled in their own modals. -->
    <PaymentModal
      :modal-id="'new-subscription-payment'"
      :selected-product="newSubscription"
      :account-profile="accountProfile"
      @submit="subscribeProduct"
      @goBack="returnToDowngrade"
      @savePremiumFeaturesGettingReadyInfo="handleDowngradeSubscriptionChange"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex'
import * as Constants from '@/constants'
import {
  isHomePremiumSubscription,
  canDowngradeToSingleGeneratorPlan,
  canDowngradeToSingleFuelMonitorPlan,
  canDowngradeToSingleHomePlan,
  subscriptionDetailContainsApparatus,
  subscriptionDetailActive,
  openModal,
  closeModal,
  pollForSubmissionUpdate,
  isDealerManaged,
  planEligibleForDowngrade,
  pendingChangeIsAlreadyActive,
  pendingChangeIsPendingButAcknowledged
} from '@/utils/mixins'
import CancelSubscriptionModal from './CancelSubscriptionModal.vue'
import PaymentCadenceModal from './PaymentCadenceModal.vue'
import DowngradeSubscriptionModal from './DowngradeSubscriptionModal.vue'
import PaymentModal from '@/components/Enrollment/Subscription/PaymentModal.vue'
import DowngradeUnavailableModal from './DowngradeUnavailableModal.vue'
import RemoveProductsSelectionModal from './RemoveProductsSelectionModal.vue'

const ManagePlanTypes = {
  CHANGE_PAYMENT_CADENCE: 'changePaymentCadence',
  REMOVE_PRODUCTS: 'removeProducts',
  DOWNGRADE_PLAN: 'downgradePlan',
  CANCEL_PLAN: 'cancelPlan'
}

/** Manage plan modal is the starting point for downgrading, removing products, doing a cadence change, or cancelling your subscription from the Plan Details page. */
export default {
  components: {
    CancelSubscriptionModal,
    PaymentCadenceModal,
    DowngradeSubscriptionModal,
    DowngradeUnavailableModal,
    PaymentModal,
    RemoveProductsSelectionModal
  },
  props: [
    /** A custom subscription data type. See usage of this component for unfortunate and confusing details. */
    'subscription'
  ],
  inject: ['updateSubscriptionDetails'],
  data() {
    return {
      managePlanType: undefined,
      newSubscription: null,
      allSubscriptionProducts: null,
      shouldDisableCadenceChange: false
    }
  },
  computed: {
    ...mapState({
      accountProfile: state => state.account.accountProfile,
      apparatuses: state => state.apparatus.apparatuses
    })
  },
  methods: {
    /** Validation function for use with global pollForSubmissionUpdate function for subscription change detection. Also returns a subscription if it finds one that matches the validation criteria.*/
    downgradePollingValidator(newSubscriptionData) {
      const subscriptionApparatusIds = this.subscription.apparatuses.map(apparatus => apparatus.id)

      return newSubscriptionData.subscriptions?.find(
        sub =>
          // Sub must be active, pending change, or pending cancel.
          subscriptionDetailActive(sub.status) &&
          // A user cannot upgrade or update a DM plan. DM plans are managed in Fleet. A DM plan is for a singular apparatus (i.e., tied to only one HSB or FM).
          !isDealerManaged(sub.product?.productId) &&
          sub.apparatuses?.length > 0 &&
          // Since they have to include all apparatuses in the plan change, we only need to check that one of those apparatuses is included.
          subscriptionDetailContainsApparatus(sub.apparatuses, subscriptionApparatusIds[0]) &&
          (pendingChangeIsAlreadyActive(sub, this.newSubscription.productId) ||
            pendingChangeIsPendingButAcknowledged(sub, this.newSubscription.productId))
      )
    },
    /**
     * Handles downgrade subscription changes on this page.
     * Called after updating subscription on the account.
     *
     * Flow:
     * 1. Polls backend for subscription updates using pollForSubmissionUpdate with a validator
     * 2. Validator checks if any active subscription contains the first apparatus (polls until found)
     * 3. Once polling completes, either:
     *    a. Finds a match:
     *       Then: Update subscription details, show green success toast,
     *    b. Does not find a match, and the subscription update is still processing:
     *       - Adds pending subscription change info to global state to alert Dashboard/SettingsSubscriptions to display the blue banner
     * @async
     * @returns {Promise<void>}
     */
    async handleDowngradeSubscriptionChange() {
      // Poll for subscription details until the conditions of the validator are met (i.e., we find a subscription that includes the updates we expect).
      this.pollForSubmissionUpdate(
        'getSubscriptionDetails',
        this.accountProfile.organizationId,
        this.downgradePollingValidator,
        'lg'
      )
        .then(newSubscriptionData => {
          const newSubscriptionDetails = this.downgradePollingValidator(newSubscriptionData)
          // Show the green toast if the API has acknowledged our change (either immediately applied or pending).
          if (newSubscriptionDetails) {
            // Clear any existing pending changes because a new plan change will overwrite anything pending.
            this.$store.dispatch('deletePendingSubscriptionChanges')

            this.updateSubscriptionDetails(newSubscriptionData)
            this.$store.dispatch('displayToast', {
              title: this.$t('xSuccess'),
              message: this.$t('xPlanChangedSuccessfully'),
              variant: 'success'
            })
          }
          // Otherwise, processing is not complete yet, show the blue banners.
          else {
            const pendingChange = {
              apparatusIds: this.subscription?.apparatuses?.map(apparatus => apparatus.id) ?? [],
              pendingProductId: this.newSubscription.productId,
              isPendingCancel: false
            }
            this.$store.dispatch('addPendingSubscriptionChanges', pendingChange)
          }
        })
        .catch(() => {
          this.showErrorToast()
        })
    },
    closeManagePlanModal() {
      this.closeModal('manage-plan-modal')
    },
    mustRemoveBeforeDowngrading() {
      this.openModal('manage-plan-modal')
      this.managePlanType = ManagePlanTypes.REMOVE_PRODUCTS
    },
    isPendingChangeSameProductDifferentCadence() {
      if (!this.subscription) {
        return false
      }
      let otherCadenceType = null
      if (this.subscription?.subscriptionProductId?.includes(Constants.SubscriptionDurations.YEARLY)) {
        otherCadenceType = Constants.SubscriptionDurations.MONTHLY
      } else {
        otherCadenceType = Constants.SubscriptionDurations.YEARLY
      }

      if (this.subscription?.subscriptionProductId && this.subscription?.subscriptionPendingProductId) {
        const currentProductId = this.subscription.subscriptionProductId
        const pendingProductId = this.subscription.subscriptionPendingProductId

        const identifiers = [
          Constants.productIdentifier.SINGLE_FM,
          Constants.productIdentifier.SINGLE_HSB,
          Constants.productIdentifier.SINGLE_HOME,
          Constants.productIdentifier.MULTI_HOME
        ]
        const hasSameProduct = identifiers.filter(identifier => {
          return (
            currentProductId.includes(identifier) &&
            pendingProductId.includes(identifier) &&
            pendingProductId.includes(otherCadenceType)
          )
        }).length

        return hasSameProduct ? true : false
      }
      return false
    },
    /** Returns true if the user already has a pending cadence change.*/
    disableCadenceChange() {
      if (
        this.subscription &&
        this.subscription.subscriptionStatus === Constants.SubscriptionDetailPendingProductChange
      ) {
        const hasAPendingCadenceChange = this.isPendingChangeSameProductDifferentCadence()
        // If user has a pending cadence change already, we do not allow further cadence changes.
        return hasAPendingCadenceChange
      }
    },
    shouldShowRemoveAndDowngradeOptions() {
      return isHomePremiumSubscription(this.subscription?.subscriptionProductId)
    },
    async openDowngradeModal() {
      let getPlan = null
      const response = await this.$store
        .dispatch('getSubscriptionDetails', this.accountProfile.organizationId)
        .catch(() => {
          this.showErrorToast()
          return
        })
      if (!response) {
        return
      }
      getPlan = response.data?.subscriptions?.find(
        subscription => subscription?.product.productId === this.subscription?.subscriptionProductId
      )
      const productsResponse = await this.$store.dispatch('getAllWebSubscriptionProducts').catch(() => {
        this.showErrorToast()
        return
      })
      if (!productsResponse) {
        return
      }
      this.allSubscriptionProducts = productsResponse.data?.products
      if (this.planEligibleForDowngrade(getPlan)) {
        if (canDowngradeToSingleHomePlan(getPlan)) {
          if (getPlan?.product.durationInMonths === 12) {
            this.newSubscription = productsResponse.data?.products.find(
              product => product.productId === Constants.HomePremiumOneYearlyProductId
            )
          } else {
            this.newSubscription = productsResponse.data?.products.find(
              product => product.productId === Constants.HomePremiumOneMonthlyProductId
            )
          }
        } else if (canDowngradeToSingleGeneratorPlan(getPlan)) {
          if (getPlan?.product.durationInMonths === 12) {
            this.newSubscription = productsResponse.data?.products.find(
              product => product.productId === Constants.SingleGeneratorYearlyProductId
            )
          } else {
            this.newSubscription = productsResponse.data?.products.find(
              product => product.productId === Constants.SingleGeneratorMonthlyProductId
            )
          }
        } else if (canDowngradeToSingleFuelMonitorPlan(getPlan)) {
          if (getPlan?.product.durationInMonths === 12) {
            this.newSubscription = productsResponse.data?.products.find(
              product => product.productId === Constants.SingleFuelMonitorYearlyProductId
            )
          } else {
            this.newSubscription = productsResponse.data?.products.find(
              product => product.productId === Constants.SingleFuelMonitorMonthlyProductId
            )
          }
        }
        this.openModal('downgrade-subscription-modal')
      } else {
        this.openModal('downgrade-unavailable-modal')
      }
    },
    proceedToPaymentForDowngrade() {
      if (this.newSubscription) {
        this.$store.dispatch('getAccountProfile')
        this.closeModal('downgrade-subscription-modal')
        this.$bvModal.show('new-subscription-payment')
      }
    },
    subscribeProduct({ resolve, reject }) {
      let subscriptionApparatuses = this.subscription.apparatuses.map(apparatus => apparatus.id)
      this.$store
        .dispatch('createPurchaseIntent', {
          productId: this.newSubscription.productId,
          organizationId: this.accountProfile.organizationId,
          apparatusIds: subscriptionApparatuses
        })
        .then(() => {
          this.$store
            .dispatch('updateSubscriptionProduct', {
              organizationId: this.accountProfile.organizationId,
              productId: this.newSubscription.productId
            })
            .then(response => {
              resolve(response)
              this.$bvModal.hide('new-subscription-payment')
            })
            .catch(() => {
              reject(this.$t('xPlanChangesFailed'))
            })
        })
        .catch(() => {
          reject(this.$t('xPlanChangesFailed'))
        })
    },
    returnToDowngrade() {
      this.$bvModal.hide('new-subscription-payment')
      this.openModal('downgrade-subscription-modal')
    },
    goToSelectedOption() {
      switch (this.managePlanType) {
        case ManagePlanTypes.CHANGE_PAYMENT_CADENCE:
          this.openModal('change-payment-cadence-modal')
          break
        case ManagePlanTypes.REMOVE_PRODUCTS:
          this.openModal('remove-apparatuses-selection-modal')
          break
        case ManagePlanTypes.DOWNGRADE_PLAN:
          this.openDowngradeModal()
          break
        case ManagePlanTypes.CANCEL_PLAN:
          this.openModal('cancel-subscription-modal')
          break
        default:
          break
      }
      this.closeManagePlanModal()
    },
    /** Show red error toast. */
    showErrorToast() {
      this.$store.dispatch('displayToast', {
        title: this.$t('xSomethingWentWrong'),
        message: this.$t('xPleaseWaitAndTryAgain'),
        variant: 'danger'
      })
    },
    openModal,
    closeModal,
    pollForSubmissionUpdate,
    planEligibleForDowngrade
  },
  async mounted() {
    if (!this.accountProfile) {
      await this.$store.dispatch('getAccountProfile')
    }
    if (!this.apparatuses) {
      await this.$store.dispatch('getApparatuses')
    }

    this.shouldDisableCadenceChange = this.disableCadenceChange()
  },
  watch: {
    subscription(newVal, oldVal) {
      if (newVal) {
        this.shouldDisableCadenceChange = this.disableCadenceChange()
      }
    }
  }
}
</script>

<style scoped lang="scss">
.radio-button-subtext {
  color: var(--carbon-600);
}
</style>
